import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { requestPasswordReset, resetPassword } from '../../services/authentication/passwordResetService';

interface PasswordResetFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

export const usePasswordReset = () => {
  const [formData, setFormData] = useState<PasswordResetFormData>({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [resetSuccess, setResetSuccess] = useState<boolean>(false);
  const params = useParams();

  const handleInputChange = (field: keyof PasswordResetFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validatePassword = (): boolean => {
    if (formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    if (formData.password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setPasswordError("Password must contain at least one lowercase letter");
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setPasswordError("Password must contain at least one uppercase letter");
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setPasswordError("Password must contain at least one number");
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      setPasswordError("Password must contain at least one special character");
      return false;
    }
    return true;
  };

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await requestPasswordReset({
        email: formData.email,
        tenantId: params.orgId || ''
      });
      setEmailSent(true);
    } catch (error) {
      console.error('Error requesting password reset:', error);
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (!validatePassword()) {
      return;
    }

    try {
      const queryParams = new URLSearchParams(window.location.search);
      const token = queryParams.get('token');

      if (!token) {
        setPasswordError('Reset token is missing');
        return;
      }

      await resetPassword({
        token,
        newPassword: formData.password,
        tenantId: params.orgId || ''
      });
      setResetSuccess(true);
    } catch (error) {
      if (error instanceof Error) {
        setPasswordError(error.message);
      } else {
        setPasswordError('Oops! Something went wrong.');
      }
    }
  };

  return {
    formData,
    emailSent,
    passwordError,
    resetSuccess,
    handleInputChange,
    handleRequestSubmit,
    handleResetSubmit
  };
}; 